import { useMemo } from "react";

import { useOutletContext } from "@zenith/context";
import { OutletListDTO } from "@zenith/services/user";

/**
 * The outlet view the merchant is on, resolved into the questions a screen
 * actually asks.
 *
 * Screens follow the dashboard's outlet view instead of keeping a selection of
 * their own, so this is the single place that answers "is the main outlet in
 * view?" or "which outlets does this request cover?".
 */
export interface OutletViewType {
  /** Every outlet the merchant has. */
  outlets: OutletListDTO[];
  isLoadingOutlets: boolean;
  isLoadingBalances: boolean;
  /** The outlets the current view covers; empty when the merchant has none. */
  selectedOutlets: OutletListDTO[];
  /** The single outlet in view, or undefined when the view covers several. */
  selectedOutlet?: OutletListDTO;
  /** True when the view covers the merchant's whole outlet list. */
  isAllOutletsSelected: boolean;
  isSingleOutletView: boolean;
  /** True when the merchant has more than one outlet — i.e. views to choose between. */
  hasMultipleOutlets: boolean;
  /** The merchant's main outlet — the one the business wallet belongs to. */
  mainOutlet?: OutletListDTO;
  /** True when the main outlet is one of the outlets in view. */
  isMainOutletInView: boolean;
  /** True when the view is exactly the main outlet, and nothing else. */
  isMainOutletView: boolean;
  /** True once the outlets are loaded and the view is exactly the outlet flagged default. */
  isDefaultOutletView: boolean;
  /**
   * The `outletId` values outlet-scoped calls should carry. Empty when the
   * view covers every outlet, since asking for all of them is the same as
   * asking for none.
   */
  outletIdsForRequests: string[];
  /**
   * The wallets of the outlets in view, for calls filtered by `walletId`:
   * every outlet's wallet on All Outlets, the one outlet's on a single view.
   */
  walletIdsForRequests: string[];
}

/**
 * Resolves the outlet context into the view a screen renders from.
 */
export const useOutletView = (): OutletViewType => {
  const { outletContext } = useOutletContext();
  const { outlets, isLoadingOutlets, isLoadingBalances, selection } =
    outletContext;

  return useMemo(() => {
    const selectedOutlets =
      selection?.kind === "all"
        ? outlets
        : selection?.kind === "outlets"
        ? outlets.filter((outlet) => selection.ids.includes(outlet.id ?? ""))
        : [];

    // The default outlet is the merchant's main one. A list that flags none
    // still has one, and the outlet switcher has always treated the first as
    // it, so both agree on which outlet is main.
    const mainOutlet = outlets.find((outlet) => outlet.isDefault) ?? outlets[0];

    const isSingleOutletView = selectedOutlets.length === 1;
    const isMainOutletInView =
      !mainOutlet ||
      selectedOutlets.some((outlet) => outlet.id === mainOutlet.id);

    const outletIdsForRequests =
      selectedOutlets.length > 0 && selectedOutlets.length < outlets.length
        ? selectedOutlets
            .map((outlet) => outlet.id)
            .filter((id): id is string => Boolean(id))
        : [];

    const walletIdsForRequests = selectedOutlets
      .map((outlet) => outlet.wallet)
      .filter((wallet): wallet is string => Boolean(wallet));

    return {
      outlets,
      isLoadingOutlets,
      isLoadingBalances,
      selectedOutlets,
      selectedOutlet: isSingleOutletView ? selectedOutlets[0] : undefined,
      isAllOutletsSelected: selection?.kind === "all",
      isSingleOutletView,
      hasMultipleOutlets: outlets.length > 1,
      mainOutlet,
      isMainOutletInView,
      isMainOutletView:
        !mainOutlet || (isSingleOutletView && isMainOutletInView),
      isDefaultOutletView:
        !isLoadingOutlets &&
        isSingleOutletView &&
        selectedOutlets[0].isDefault === true,
      outletIdsForRequests,
      walletIdsForRequests,
    };
  }, [outlets, isLoadingOutlets, isLoadingBalances, selection]);
};
